import { z } from 'zod';

import { nonNegativeIntSchema, sha256HexSchema, uuidSchema } from '../common/primitives';

/**
 * Das vom Worker gemeldete Ist-Inventar.
 *
 * Der Master vergleicht dieses Inventar mit seinem gespeicherten Soll-Zustand.
 * Die Differenz ist die Arbeitsliste des Reconcilers.
 *
 * Bewusste Entscheidung: Der Heartbeat trägt **immer das vollständige
 * Inventar**, nie ein Delta. Deltas wären sparsamer und wären der Fehler —
 * nach einem Netzwerkausfall oder einem Agent-Neustart ist ein Voll-Snapshot
 * das Einzige, dem man ohne Zusatzannahmen trauen kann. Bei 200 Sites sind das
 * rund 30 KB alle 10 Sekunden; das ist irrelevant.
 */

/** Beobachteter Zustand eines Site-Stacks auf dem Worker. */
export const SITE_RUNTIME_STATE = {
  /** Alle Container laufen und sind gesund. */
  running: 'running',
  /** Stack existiert, Container sind gestoppt. */
  stopped: 'stopped',
  /** Stack wird gerade aufgebaut oder verändert. */
  transitioning: 'transitioning',
  /** Stack existiert, aber mindestens ein Container ist ungesund oder gecrasht. */
  unhealthy: 'unhealthy',
  /** Der Agent kann den Zustand nicht bestimmen (z. B. Docker nicht erreichbar). */
  unknown: 'unknown',
} as const;

export type SiteRuntimeState = (typeof SITE_RUNTIME_STATE)[keyof typeof SITE_RUNTIME_STATE];

export const siteRuntimeStateSchema = z.enum([
  SITE_RUNTIME_STATE.running,
  SITE_RUNTIME_STATE.stopped,
  SITE_RUNTIME_STATE.transitioning,
  SITE_RUNTIME_STATE.unhealthy,
  SITE_RUNTIME_STATE.unknown,
]);

/** Ein Eintrag des Inventars: eine auf diesem Node vorgefundene Site. */
export const siteInventoryEntrySchema = z.object({
  siteId: uuidSchema,
  state: siteRuntimeStateSchema,
  /**
   * Fingerabdruck der Spezifikation, die auf diesem Node tatsächlich angewandt
   * wurde — gelesen aus dem Container-Label, nicht aus einer Notiz des Agents.
   *
   * Das ist der Dreh- und Angelpunkt des Reconcilers: Damit wird "läuft dort das
   * Richtige, und ist die Operation fertig?" zu einer **Beobachtung** statt zu
   * einer Zusage aus einer Rückmeldung. Diese Antwort übersteht verlorene
   * HTTP-Antworten, Master-Neustarts und Redis-Verlust.
   *
   * `null`, wenn der Agent den Stack vorfindet, aber kein Label lesen kann —
   * etwa bei einem Rest aus einer abgebrochenen Operation.
   */
  specHash: sha256HexSchema.nullable(),
  /** Letzte Operation, die diesen Stack angefasst hat; aus dem Container-Label. */
  lastOperationId: uuidSchema.nullable(),
  /** Namen der Container, die nicht im erwarteten Zustand sind. */
  unhealthyContainers: z.array(z.string()).default([]),
  /** Tatsächlicher Ressourcenverbrauch, für Missbrauchserkennung und Tarifprüfung. */
  cpuPercent: z.number().nonnegative().nullable(),
  memoryUsedMb: nonNegativeIntSchema.nullable(),
  diskUsedMb: nonNegativeIntSchema.nullable(),
});

export type SiteInventoryEntry = z.infer<typeof siteInventoryEntrySchema>;

/**
 * Vollständiges Inventar eines Nodes.
 *
 * Die Obergrenze ist kein Schönheitswert: Ohne sie kann ein fehlerhafter oder
 * kompromittierter Agent den Master mit einem beliebig großen Payload
 * beschäftigen. `maxSites` liegt in der Praxis weit darunter.
 */
export const nodeInventorySchema = z
  .array(siteInventoryEntrySchema)
  .max(2000, { message: 'Inventar überschreitet die zulässige Größe.' });

export type NodeInventory = z.infer<typeof nodeInventorySchema>;
